/**
 * features/members/hooks.ts — React Query hooks for the Members feature.
 *
 * All tenant-scoped reads key on tenantId so cross-tenant accidents wipe
 * the cache cleanly. Add is a mutation that invalidates the list on
 * success; the freshly-added Member appears next render.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query';
import { useAuthStore } from '@/stores/auth';
import {
  archiveMember,
  cancelChatMessage,
  clearChatThread,
  enhance,
  getMember,
  getRecommendedJobs,
  getStaffProfileDetail,
  addMember,
  addHuman,
  linkAttachmentToMember,
  listChatMessages,
  listMemberAttachments,
  listMemberMemories,
  listMembers,
  listPositionsCatalog,
  listRetrievedResources,
  listStaffProfiles,
  listUnitsCatalog,
  matchStaffProfiles,
  recordMemory,
  sendChatMessage,
  unlinkAttachmentFromMember,
  updateMember,
} from './api';
import type {
  ArchiveResponse,
  ChatCancelResponse,
  ChatClearResponse,
  ChatSendResponse,
  ClearChatOptions,
  EnhanceKind,
  EnhanceResponse,
  AddHumanBody,
  AddMemberRequest,
  AddMemberResponse,
  MatchRequest,
  MatchResponse,
  RecordMemoryResponse,
  StaffProfileDetailResponse,
  UpdateMemberRequest,
  UpdateResponse,
} from './schemas';

/** Returns the active tenant id; consumers should be inside RequireAuth. */
export function useActiveTenantId(): string {
  const tenantId = useAuthStore((s) => s.tenantUser?.tenantId);
  if (!tenantId) {
    // RequireAuth guards every member route; this is a programmer error
    // (calling the hook from a public route), not a runtime user state.
    throw new Error('useActiveTenantId called without an active tenant');
  }
  return tenantId;
}

export function useMembers() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: queryKeys.members.list(tenantId),
    queryFn: () => listMembers(tenantId),
  });
}

export function useMember(memberId: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: queryKeys.members.detail(memberId ?? '__none'),
    queryFn: () => getMember(tenantId, memberId!),
    enabled: Boolean(memberId),
  });
}

export function useRecommendedJobs(memberId: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['members', tenantId, 'recommended-jobs', memberId ?? '__none'] as const,
    queryFn: () => getRecommendedJobs(tenantId, memberId!),
    enabled: Boolean(memberId),
    staleTime: 60 * 1000,
  });
}

// Staff profiles are system-scoped data, but the endpoint still requires
// X-Tenant-Id (any authenticated tenant user can read). Key on tenantId
// to scope the cache; the payload is small and identical across tenants.
export function useStaffProfiles() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['staff-profiles', tenantId] as const,
    queryFn: () => listStaffProfiles(tenantId),
    // Catalog data; rarely changes within a session.
    staleTime: 5 * 60_000,
  });
}

export function useStaffProfileDetail(id: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery<StaffProfileDetailResponse>({
    queryKey: ['staff-profiles', tenantId, 'detail', id ?? '__none'] as const,
    queryFn: () => getStaffProfileDetail(tenantId, id!),
    enabled: Boolean(id),
  });
}

export function useAddMember() {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<AddMemberResponse, unknown, AddMemberRequest>({
    mutationFn: (values) => addMember(tenantId, values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.members.list(tenantId) });
    },
  });
}

export function useAddHuman() {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<AddMemberResponse, unknown, AddHumanBody>({
    mutationFn: (body) => addHuman(tenantId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.members.list(tenantId) });
    },
  });
}

interface UpdateArgs {
  memberId: string;
  patch: UpdateMemberRequest;
}

export function useUpdateMember() {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<UpdateResponse, unknown, UpdateArgs>({
    mutationFn: ({ memberId, patch }) => updateMember(tenantId, memberId, patch),
    onSuccess: async (_data, { memberId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.members.list(tenantId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.members.detail(memberId) }),
      ]);
    },
  });
}

export function useArchiveMember() {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<ArchiveResponse, unknown, { memberId: string }>({
    mutationFn: ({ memberId }) => archiveMember(tenantId, memberId),
    onSuccess: async (_data, { memberId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.members.list(tenantId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.members.detail(memberId) }),
      ]);
    },
  });
}

// Position / Unit catalogs are tenant-merged but mostly static within a
// session — long staleTime so the picker doesn't refetch on every render.
export function usePositionsCatalog() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['positions-catalog', tenantId] as const,
    queryFn: () => listPositionsCatalog(tenantId),
    staleTime: 5 * 60_000,
  });
}

export function useUnitsCatalog() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['units-catalog', tenantId] as const,
    queryFn: () => listUnitsCatalog(tenantId),
    staleTime: 5 * 60_000,
  });
}

export function useEnhance() {
  const tenantId = useActiveTenantId();
  return useMutation<
    EnhanceResponse,
    unknown,
    { kind: EnhanceKind; content: string; userRequest?: string }
  >({
    mutationFn: (args) => enhance(tenantId, args),
  });
}

export function useMatchStaffProfiles() {
  const tenantId = useActiveTenantId();
  return useMutation<MatchResponse, unknown, MatchRequest>({
    mutationFn: (body) => matchStaffProfiles(tenantId, body),
  });
}

export function useChatMessages(memberId: string) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['chat-messages', tenantId, memberId] as const,
    queryFn: () => listChatMessages(tenantId, { memberId, limit: 50 }),
  });
}

export function useSendChatMessage(memberId: string) {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<
    ChatSendResponse,
    unknown,
    { content: string; clientMessageId?: string; attachment_ids?: string[] }
  >({
    mutationFn: ({ content, clientMessageId, attachment_ids }) =>
      sendChatMessage(tenantId, { memberId, content, clientMessageId, attachment_ids }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['chat-messages', tenantId, memberId] });
    },
  });
}

export function useCancelChatMessage() {
  const tenantId = useActiveTenantId();
  return useMutation<ChatCancelResponse, unknown, { messageId: string }>({
    mutationFn: ({ messageId }) => cancelChatMessage(tenantId, messageId),
  });
}

export function useClearChatThread(memberId: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation<ChatClearResponse, unknown, ClearChatOptions>({
    mutationFn: (options: ClearChatOptions) => clearChatThread(tenantId, memberId, options),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat-messages', tenantId, memberId] }),
  });
}

interface RecordMemoryArgs {
  content: string;
  source: { kind: 'chat_pin'; id: string };
}

export function useRecordMemory(memberId: string) {
  const tenantId = useActiveTenantId();
  const queryClient = useQueryClient();
  return useMutation<RecordMemoryResponse, unknown, RecordMemoryArgs>({
    mutationFn: (body) => recordMemory(tenantId, memberId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['member-memories', tenantId, memberId],
      });
    },
  });
}

export function useMemberMemories(memberId: string) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['member-memories', tenantId, memberId] as const,
    queryFn: () => listMemberMemories(tenantId, memberId),
    staleTime: 30_000,
  });
}

export function useRetrievedResources(chatMessageId: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['retrieved-resources', tenantId, chatMessageId ?? '__none'] as const,
    queryFn: () => listRetrievedResources(tenantId, chatMessageId!),
    enabled: Boolean(chatMessageId),
    staleTime: 60_000,
  });
}

export function useMemberAttachments(memberId: string | null) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['member-attachments', tenantId, memberId] as const,
    queryFn: () => listMemberAttachments(tenantId, memberId!),
    enabled: Boolean(memberId),
  });
}

export function useLinkMemberAttachment(memberId: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ attachmentId }: { attachmentId: string }) =>
      linkAttachmentToMember(tenantId, memberId, attachmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['member-attachments', tenantId, memberId] }),
  });
}

export function useUnlinkMemberAttachment(memberId: string) {
  const tenantId = useActiveTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ attachmentId }: { attachmentId: string }) =>
      unlinkAttachmentFromMember(tenantId, memberId, attachmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['member-attachments', tenantId, memberId] }),
  });
}
